/**
 * Deeplink Validation Utilities
 * 
 * Functions to validate deeplink IDs and check if resources exist.
 */

import logger from '../logger';

/**
 * Validates if a note ID exists in the app state
 * @param {Object} appState - Current app state context
 * @param {string} noteId - Note ID to validate
 * @returns {Promise<{valid: boolean, note: Object|null}>}
 */
export const validateNoteId = async (appState, noteId) => {
  if (!noteId) {
    return { valid: false, note: null };
  }

  try {
    // Check if note exists in context
    const { notes } = appState;
    if (notes && notes[noteId]) {
      return { valid: true, note: notes[noteId] };
    }

    // If not in context, try to fetch from cloud (if online)
    if (navigator.onLine) {
      try {
        const { fetchNoteFromCloud } = await import('../state/cloud/notes');
        const note = await fetchNoteFromCloud({ noteId });
        if (note && note.note_id) {
          return { valid: true, note };
        }
      } catch (error) {
        logger.warn({ noteId, error }, 'Failed to fetch note from cloud for validation');
      }
    }

    return { valid: false, note: null };
  } catch (error) {
    logger.error({ noteId, error }, 'Error validating note ID');
    return { valid: false, note: null };
  }
};

/**
 * Validates if a notebook ID exists in the app state
 * @param {Object} appState - Current app state context
 * @param {string} notebookId - Notebook ID to validate
 * @returns {Promise<{valid: boolean, notebook: Object|null}>}
 */
export const validateNotebookId = async (appState, notebookId) => {
  if (!notebookId) {
    return { valid: false, notebook: null };
  }

  try {
    // Check if notebook exists in context
    const { notebooks } = appState;
    if (notebooks && notebooks[notebookId]) {
      return { valid: true, notebook: notebooks[notebookId] };
    }

    // For now, we'll just check context. If not found, consider it invalid.
    // In the future, we could add a fetch function for individual notebooks.
    return { valid: false, notebook: null };
  } catch (error) {
    logger.error({ notebookId, error }, 'Error validating notebook ID');
    return { valid: false, notebook: null };
  }
};

/**
 * Validates if an area of focus ID exists in the app state
 * @param {Object} appState - Current app state context
 * @param {string} aofId - Area of focus ID to validate
 * @returns {Promise<{valid: boolean, aof: Object|null}>}
 */
export const validateAreaOfFocusId = async (appState, aofId) => {
  if (!aofId) {
    return { valid: false, aof: null };
  }

  try {
    // Check if AOF exists in context
    const { areasOfFocusCollections } = appState;
    if (areasOfFocusCollections && areasOfFocusCollections[aofId]) {
      return { valid: true, aof: areasOfFocusCollections[aofId] };
    }

    // For now, we'll just check context. If not found, consider it invalid.
    // In the future, we could add a fetch function for individual AOFs.
    return { valid: false, aof: null };
  } catch (error) {
    logger.error({ aofId, error }, 'Error validating area of focus ID');
    return { valid: false, aof: null };
  }
};

/**
 * Validates a deeplink URL state
 * @param {Object} appState - Current app state context
 * @param {Object} urlState - Decoded URL state from permalink
 * @returns {Promise<{valid: boolean, reason: string|null, invalidId: string|null}>}
 */
export const validateDeeplink = async (appState, urlState) => {
  if (!urlState) {
    return { valid: false, reason: 'invalid_url', invalidId: null };
  }

  // Validate note ID if present
  if (urlState.selectedNoteId) {
    const noteValidation = await validateNoteId(appState, urlState.selectedNoteId);
    if (!noteValidation.valid) {
      return {
        valid: false,
        reason: 'note_not_found',
        invalidId: urlState.selectedNoteId,
      };
    }
  }

  // Validate notebook ID if present
  if (urlState.selectedNotebookId) {
    const notebookValidation = await validateNotebookId(appState, urlState.selectedNotebookId);
    if (!notebookValidation.valid) {
      return {
        valid: false,
        reason: 'notebook_not_found',
        invalidId: urlState.selectedNotebookId,
      };
    }
  }

  // Validate area of focus ID if present
  if (urlState.selectedAreaOfFocusId) {
    const aofValidation = await validateAreaOfFocusId(appState, urlState.selectedAreaOfFocusId);
    if (!aofValidation.valid) {
      return {
        valid: false,
        reason: 'aof_not_found',
        invalidId: urlState.selectedAreaOfFocusId,
      };
    }
  }

  return { valid: true, reason: null, invalidId: null };
};

